import {
  ArrowLeftRight,
  BarChart3,
  Bell,
  Cloud,
  FileClock,
  FileCog,
  LayoutDashboard,
  ListVideo,
  PlusCircle,
  Radio,
  ShieldCheck,
  SlidersHorizontal,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

export interface LiveNavEntry {
  slug: string;
  label: string;
  icon: LucideIcon;
  disabled?: boolean;
  /** Trailing chip ("Soon") — visible cue for disabled entries until @ui
   * SidebarItem styles `disabled` itself. */
  badge?: string;
}

export interface LiveNavGroup {
  heading?: string;
  entries: LiveNavEntry[];
}

/**
 * Live Transcoding console sidebar — mirrors the legacy grouping:
 * Dashboard · live-transcoding workflow · delivery settings · analysis · audit.
 * Provisioning stays visible but disabled (legacy parity).
 *
 * Dashboard uses empty slug ('') so `liveHref(base, '')` returns `base`
 * — same convention as the WAAP console.
 */
export const LIVE_NAV: LiveNavGroup[] = [
  {
    entries: [{ slug: '', label: 'Dashboard', icon: LayoutDashboard }],
  },
  {
    heading: 'Live Transcoding',
    entries: [
      { slug: 'transcoding-presets', label: 'Transcoding Presets', icon: SlidersHorizontal },
      { slug: 'current-jobs', label: 'Current Jobs', icon: ListVideo },
      { slug: 'event-jobs/create', label: 'Create Event Job', icon: PlusCircle },
      { slug: 'linear-jobs/create', label: 'Create Linear Job', icon: Radio },
    ],
  },
  {
    heading: 'Settings',
    entries: [
      { slug: 'file-transfer', label: 'File Transfer', icon: ArrowLeftRight },
      { slug: 'notifications', label: 'Notifications', icon: Bell },
      { slug: 'domain-templates', label: 'Domain Templates', icon: FileCog },
      { slug: 'cdn-endpoints', label: 'CDN Endpoints', icon: Cloud },
      { slug: 'verimatrix-drms', label: 'Verimatrix DRMs', icon: ShieldCheck },
      { slug: 'provisioning', label: 'Provisioning', icon: Wrench, disabled: true, badge: 'Soon' },
    ],
  },
  {
    heading: 'Analysis',
    entries: [{ slug: 'analysis', label: 'Analysis', icon: BarChart3 }],
  },
  {
    heading: 'Audit',
    entries: [{ slug: 'audit', label: 'Audit Log', icon: FileClock }],
  },
];

/** Absolute href for a slug under the console base ('' slug = base/root = Dashboard). */
export function liveHref(base: string, slug: string): string {
  return slug ? `${base}/${slug}` : base;
}

/** All selectable hrefs flattened from the nav. */
function allHrefs(base: string): string[] {
  return LIVE_NAV.flatMap((g) => g.entries.map((e) => liveHref(base, e.slug)));
}

/**
 * Active entry href for the current pathname (longest-prefix match).
 * `base` and `base/dashboard` both resolve to the Dashboard entry ('' slug).
 */
export function liveActiveId(base: string, pathname: string): string {
  const normalized = pathname === `${base}/dashboard` ? base : pathname;
  const matches = allHrefs(base)
    .filter((href) => normalized === href || normalized.startsWith(`${href}/`))
    .sort((a, b) => b.length - a.length);
  return matches[0] ?? base;
}
